import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { IdeaListItem, Permission } from '@shared/types';
import { IDEA_STATUS_LABELS, IDEA_STATUS_TONES } from '@shared/ideaStatus';
import type { Tone } from '@shared/format';
import { formatRate, relativeTime, tagTone } from '@shared/format';
import { useAuthStore } from '../../app/authStore';
import { PageContainer } from '../../app/AppShell';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner } from '../../components/Card';
import { useSettingsStore } from '../admin/settingsStore';
import { EventFeed } from '../ideas/components/EventFeed';
import { greetingFor, useHomeData } from './homeData';

/**
 * 首页。
 *
 * 它回答三个问题：现在平台上在发生什么（协作数据）、有什么在等我（需要我看的）、
 * 我接下来最该点哪个按钮（快速入口）。
 *
 * 配色上这里比其它页面活一些：参考图的紫蓝配色、纯白大圆角卡片、
 * 紫色到蓝色的渐变主按钮，全部只在首页生效（换肤的机制见 styles/index.css）。
 * 五个创意色相（violet / blue / mint / coral / amber）只用于装饰 ——
 * 入口卡的图标底、指标卡的顶线与标签、区块标记与「有新动态」这类提示；
 * 状态与告警仍然只用语义色 —— 颜色可以表达情绪，但不该抢走判断标准的语义。
 */

const CREATIVE_TONES = {
  violet: {
    chip: 'bg-crea-violet-soft text-crea-violet-ink',
    text: 'text-crea-violet-ink',
    border: 'hover:border-crea-violet/50',
    bar: 'bg-crea-violet',
  },
  blue: {
    chip: 'bg-crea-blue-soft text-crea-blue-ink',
    text: 'text-crea-blue-ink',
    border: 'hover:border-crea-blue/50',
    bar: 'bg-crea-blue',
  },
  mint: {
    chip: 'bg-crea-mint-soft text-crea-mint-ink',
    text: 'text-crea-mint-ink',
    border: 'hover:border-crea-mint/50',
    bar: 'bg-crea-mint',
  },
  coral: {
    chip: 'bg-crea-coral-soft text-crea-coral-ink',
    text: 'text-crea-coral-ink',
    border: 'hover:border-crea-coral/50',
    bar: 'bg-crea-coral',
  },
  amber: {
    chip: 'bg-crea-amber-soft text-crea-amber-ink',
    text: 'text-crea-amber-ink',
    border: 'hover:border-crea-amber/50',
    bar: 'bg-crea-amber',
  },
} as const;

type CreativeTone = keyof typeof CREATIVE_TONES;

/** 状态分布条沿用语义色：颜色在这里表示「进行到哪一步」，不参与创意表达。 */
const TONE_BARS: Readonly<Record<Tone, string>> = {
  neutral: 'bg-line-strong',
  accent: 'bg-accent',
  ok: 'bg-ok',
  info: 'bg-info',
  caution: 'bg-caution',
  warning: 'bg-warning',
  critical: 'bg-critical',
};

const ICON_PEN = 'M3 17.25V21h3.75L18.8 8.94l-3.74-3.75L3 17.25zM20.7 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z';
const ICON_BOARD = 'M3 3h8v18H3V3zm10 0h8v11h-8V3z';
const ICON_MINE = 'M12 11a4 4 0 100-8 4 4 0 000 8zm0 2c-4.2 0-8 2.6-8 6h16c0-3.4-3.8-6-8-6z';
const ICON_STATS = 'M3 20h18v2H3v-2zM6 18h3V9H6v9zm5 0h3V4h-3v14zm5 0h3v-6h-3v6z';

/** 参考图里每个入口右下角都有一个「斜向开门」的箭头，指向「点进去」。 */
const ARROW_PATH = 'M7 17 17 7M9 7h8v8';

interface QuickAction {
  to: string;
  label: string;
  description: string;
  tone: CreativeTone;
  icon: string;
  /** 需要某个权限才显示（例如没有接口权限的成员看不到「提出创意」） */
  permission?: Permission;
}

const QUICK_ACTIONS: readonly QuickAction[] = [
  { to: '/ideas/new', label: '提出创意', description: '写清核心假设与成功指标，一分钟提交', tone: 'violet', icon: ICON_PEN, permission: 'idea.create' },
  { to: '/board', label: '创意看板', description: '看每个人在推进什么，从待验证到已上线', tone: 'blue', icon: ICON_BOARD },
  { to: '/mine', label: '我的创意', description: '我提的、我参与的、我认领的', tone: 'mint', icon: ICON_MINE },
  { to: '/stats', label: '数据看板', description: 'AI 通过率、状态分布与最近动态', tone: 'amber', icon: ICON_STATS },
];

function Icon({ path, className = '' }: { path: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={['size-4', className].join(' ')}>
      <path d={path} fill="currentColor" />
    </svg>
  );
}

function ArrowIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={['size-3.5 shrink-0', className].join(' ')}
    >
      <path d={ARROW_PATH} />
    </svg>
  );
}

function QuickActionCard({ action }: { action: QuickAction }) {
  const tone = CREATIVE_TONES[action.tone];
  return (
    <Link to={action.to} className="group block">
      <Card interactive className={['theme-home-card h-full', tone.border].join(' ')}>
        <CardBody className="flex h-full flex-col gap-2.5">
          <span className="flex items-start justify-between gap-2">
            <span className={['flex size-9 items-center justify-center rounded-xl', tone.chip].join(' ')}>
              <Icon path={action.icon} />
            </span>
            <ArrowIcon className="mt-1 text-fg-subtle transition-colors group-hover:text-accent" />
          </span>
          <span className="text-sm font-semibold text-fg">{action.label}</span>
          <span className="text-[11px] leading-relaxed text-fg-muted">{action.description}</span>
        </CardBody>
      </Card>
    </Link>
  );
}

function MetricCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string | number;
  hint: string;
  tone: CreativeTone;
}) {
  const palette = CREATIVE_TONES[tone];
  return (
    <Card className="theme-home-card overflow-hidden">
      <div aria-hidden className={['h-1', palette.bar].join(' ')} />
      <CardBody className="flex flex-col gap-1">
        <span className={['text-[11px] font-medium tracking-wide uppercase', palette.text].join(' ')}>{label}</span>
        <span className="tabular text-2xl leading-tight font-semibold text-fg">{value}</span>
        <span className="text-[11px] leading-relaxed text-fg-subtle">{hint}</span>
      </CardBody>
    </Card>
  );
}

function HighlightChip({ to, tone, children }: { to: string; tone: CreativeTone; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={[
        'inline-flex items-center gap-1.5 rounded-full border border-transparent px-2.5 py-1 text-[11px] transition-colors',
        CREATIVE_TONES[tone].chip,
        'hover:border-current/30',
      ].join(' ')}
    >
      {children}
    </Link>
  );
}

function NeedsAttention({ unread }: { unread: readonly IdeaListItem[] }) {
  if (unread.length === 0) {
    return (
      <EmptyState
        title="没有等你回复的创意"
        description="你参与过的创意有新的评论或状态变化时，会出现在这里。"
        action={
          <Link to="/board">
            <Button size="sm" variant="default">
              去逛逛看板
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <ul className="flex flex-col gap-2.5">
      {unread.slice(0, 5).map((idea) => (
        <li key={idea.id}>
          <Link to={`/ideas/${idea.id}`} className="flex items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-surface-2">
            <span aria-hidden className="mt-1.5 size-1.5 shrink-0 rounded-full bg-crea-coral" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-fg">{idea.title}</span>
              <span className="mt-0.5 block text-[10px] text-fg-subtle">
                {IDEA_STATUS_LABELS[idea.status]} · {idea.authorName} · 更新于 {relativeTime(idea.updatedAt)}
              </span>
            </span>
          </Link>
        </li>
      ))}
      {unread.length > 5 ? (
        <li className="px-2 text-[11px] text-fg-subtle">
          还有 {unread.length - 5} 条，
          <Link to="/mine" className="text-accent hover:underline">
            去「我参与的」看全部
          </Link>
        </li>
      ) : null}
    </ul>
  );
}

export function HomePage() {
  const user = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const settings = useSettingsStore((state) => state.settings);
  const loadSettings = useSettingsStore((state) => state.load);
  const { stats, highlights, loading, error } = useHomeData();

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const greeting = useMemo(() => greetingFor(new Date().getHours()), []);
  const actions = QUICK_ACTIONS.filter((action) => !action.permission || permissions.includes(action.permission));

  const decided = stats.ai.goCount + stats.ai.needsMoreInfoCount + stats.ai.noGoCount;
  const inValidation = stats.byStatus.find((item) => item.status === 'validating')?.count ?? 0;
  const maxStatus = Math.max(1, ...stats.byStatus.map((item) => item.count));
  const isEmptyPlatform = !loading && stats.total === 0;

  return (
    <PageContainer>
      <Card className="theme-home-card hero-gradient overflow-hidden">
        <CardBody className="flex flex-col gap-4 p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs text-fg-muted">
                {greeting}
                {user ? `，${user.name}` : ''}
              </p>
              <h1 className="mt-1.5 text-2xl leading-snug font-semibold text-fg">
                <span className="theme-home-title-gradient">提出创意</span>，先让 AI 把代价和风险摆出来
              </h1>
              <p className="mt-2 max-w-2xl text-xs leading-relaxed text-fg-muted">
                一份称职的创意只需要五件事：做什么、为什么现在做、面向谁、核心假设是什么、用什么指标算成功。
                写清这五件事，几分钟就能拿到一份可评审的结论与产物草稿。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {permissions.includes('idea.create') ? (
                <Link to="/ideas/new">
                  <Button variant="primary" className="btn-cta-gradient">
                    提出创意
                    <ArrowIcon />
                  </Button>
                </Link>
              ) : null}
              <Link to="/board">
                <Button variant="default">创意看板</Button>
              </Link>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {loading ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-fg-subtle">
                <Spinner className="size-3" /> 正在汇总协作数据…
              </span>
            ) : highlights.unread.length > 0 ? (
              <HighlightChip to="/mine" tone="coral">
                {highlights.unread.length} 条创意有新动态
              </HighlightChip>
            ) : null}

            {!loading && highlights.awaitingValidation > 0 ? (
              <HighlightChip to="/board" tone="violet">
                {highlights.awaitingValidation} 条还在等第一次研判
              </HighlightChip>
            ) : null}

            {!loading && highlights.assignedInProgress > 0 ? (
              <HighlightChip to="/mine" tone="mint">
                你认领的 {highlights.assignedInProgress} 条在推进
              </HighlightChip>
            ) : null}

            {!loading && highlights.drafts > 0 ? (
              <HighlightChip to="/mine" tone="amber">
                {highlights.drafts} 份草稿没提交
              </HighlightChip>
            ) : null}

            {!loading &&
            highlights.unread.length === 0 &&
            highlights.awaitingValidation === 0 &&
            highlights.assignedInProgress === 0 &&
            highlights.drafts === 0 ? (
              <span className="text-[11px] text-fg-subtle">没有待处理事项，可以挑一个想法开始写。</span>
            ) : null}

            {!loading && inValidation > 0 ? (
              <span className="text-[11px] text-fg-subtle">（{inValidation} 条正在研判）</span>
            ) : null}
          </div>
        </CardBody>
      </Card>

      {error ? <p className="text-xs text-critical">{error}</p> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {actions.map((action) => (
          <QuickActionCard key={action.to} action={action} />
        ))}
      </div>

      {isEmptyPlatform ? (
        <Card className="theme-home-card">
          <CardBody>
            <EmptyState
              title="平台还没有任何创意"
              description="从一件让你反复加班的小事开始写，比从「战略方向」开始写有用得多。提交后可以直接发起 AI 研判，几十秒就有结论。"
              action={
                permissions.includes('idea.create') ? (
                  <Link to="/ideas/new">
                    <Button variant="primary" className="btn-cta-gradient">
                      提出第一条创意
                      <ArrowIcon />
                    </Button>
                  </Link>
                ) : null
              }
            />
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="创意总量"
              value={stats.total}
              hint={`${stats.people.authors} 位成员提过创意，${stats.people.assignees} 条已有人认领`}
              tone="violet"
            />
            <MetricCard
              label="AI 通过率"
              value={formatRate(stats.ai.passRate)}
              hint={decided > 0 ? `${stats.ai.goCount} / ${decided} 份最新结论建议推进` : '还没有可用的结论'}
              tone="mint"
            />
            <MetricCard
              label="平均综合分"
              value={stats.ai.avgScore ?? '—'}
              hint="按每个创意的最新一份结论计算，不重复计分"
              tone="coral"
            />
            <MetricCard
              label="研判次数"
              value={stats.ai.runs}
              hint={`成功 ${stats.ai.succeeded} · 失败 ${stats.ai.failed}（失败不占额度）`}
              tone="amber"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="theme-home-card">
              <CardHeader>
                <CardTitle subtitle="草稿与归档不参与推进节奏">状态分布</CardTitle>
              </CardHeader>
              <CardBody>
                {stats.byStatus.length === 0 ? (
                  <EmptyState title="还没有创意" />
                ) : (
                  <ul className="flex flex-col gap-2.5">
                    {stats.byStatus.map((item) => (
                      <li key={item.status} className="flex flex-col gap-1">
                        <div className="flex items-baseline justify-between text-[11px]">
                          <span className="text-fg-muted">{IDEA_STATUS_LABELS[item.status]}</span>
                          <span className="tabular text-fg">{item.count}</span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                          <div
                            className={['h-full rounded-full', TONE_BARS[IDEA_STATUS_TONES[item.status]]].join(' ')}
                            style={{ width: `${Math.round((item.count / maxStatus) * 100)}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card className="theme-home-card">
              <CardHeader
                actions={
                  highlights.unread.length > 0 ? <Badge tone="critical">{highlights.unread.length} 条新动态</Badge> : null
                }
              >
                <CardTitle subtitle="你参与过的创意里的新评论与状态变化">需要我看的</CardTitle>
              </CardHeader>
              <CardBody>
                <NeedsAttention unread={highlights.unread} />
              </CardBody>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="theme-home-card">
              <CardHeader>
                <CardTitle subtitle="看板上筛选用的就是这些标签，计数不含已归档">大家在做什么</CardTitle>
              </CardHeader>
              <CardBody className="flex flex-col gap-3">
                {stats.byTag.length === 0 ? (
                  <EmptyState
                    title="还没有标签"
                    description={
                      settings.tags.length > 0
                        ? '提交创意时选一个分类标签，这里就能看出团队的关注点分布。'
                        : '管理员可以在「系统设置」里先建几个分类标签。'
                    }
                  />
                ) : (
                  <ul className="flex flex-wrap items-center gap-1.5">
                    {stats.byTag.map((item) => (
                      <li key={item.tag} className="flex items-center gap-1.5">
                        <Badge tone={tagTone(item.tag)}>{item.tag}</Badge>
                        <span className="tabular text-[11px] text-fg-muted">{item.count}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="border-t border-line pt-3">
                  <p className="mb-2 text-[11px] font-medium text-fg-muted">平台在关注什么</p>
                  <ul className="flex list-disc flex-col gap-1 pl-4 text-[11px] leading-relaxed text-fg-muted">
                    <li>
                      共 {stats.total} 条创意，其中 {inValidation} 条正在研判、{highlights.awaitingValidation} 条还没跑过研判。
                    </li>
                    <li>
                      AI 已给出 {decided} 份最新结论，{stats.ai.goCount} 份建议推进、{stats.ai.noGoCount} 份不建议推进。
                    </li>
                    <li>{stats.people.assignees} 条创意已经有人认领，正在推进中。</li>
                  </ul>
                </div>
              </CardBody>
            </Card>

            <Card className="theme-home-card">
              <CardHeader
                actions={
                  <Link to="/stats">
                    <Button size="sm" variant="ghost">
                      全部统计
                    </Button>
                  </Link>
                }
              >
                <CardTitle subtitle="提出、研判、流转、讨论都会记一条">最近发生了什么</CardTitle>
              </CardHeader>
              <CardBody>
                <EventFeed events={stats.recentEvents.slice(0, 8)} />
              </CardBody>
            </Card>
          </div>
        </>
      )}

      <p className="text-[11px] leading-relaxed text-fg-subtle">
        首页的数字全部来自平台自身的记录：通过率只统计每个创意的最新一份研判结论，
        重复研判不会把分母灌水；也不做人均产出与排名 —— 创意量不该是考核指标。
      </p>
    </PageContainer>
  );
}
